import cx from 'classnames';
import { Form, Button, Row } from 'antd';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { Store } from '../../state/store.interfaces';
import ActionButton from '../../common/components/buttons/ActionButton';
import { MultifactorAuthenticationMethod } from '../Settings/TabMFA/helpers';
import { LrvOtpInput } from '../../common/components/LrvOtpInput/LrvOtpInput';

import * as authSlice from './authSlice';
import styles from './MFAForm.module.scss';
import { VerificationMethodForm } from './VerificationMethodForm';

export const MFAForm = () => {
  const [t] = useTranslation();
  const dispatch = useDispatch();
  const {
    id,
    mfaAuthenticator,
    mfaEmail,
    isLoadingSetupMfa,
    errorSetupMfa,
    mfaMethod,
    mfaAvailable,
    selectedMethod,
    isFetchingVerificationCode,
  } = useSelector((state: Store) => state.auth);
    
  const [shouldAutoFocus, setShouldAutoFocus] = useState(true);
  const [code, setCode] = useState('');

  const [form] = Form.useForm();

  useEffect(() => {
    const timer = setTimeout(() => {
      setShouldAutoFocus(true);
    }, 100);

    return () => clearTimeout(timer);
  }, [selectedMethod]);

  const getInputLabelCode = () => {
    if (mfaAuthenticator?.enabled && mfaEmail?.enabled) {
      switch (selectedMethod) {
        case MultifactorAuthenticationMethod.AUTHENTICATOR_APP:
        default: {
          return t('mfa.codeMFA');
        }
        
        case MultifactorAuthenticationMethod.EMAIL: {
          return t('mfa.code');
        }
      }
    }

    if (mfaEmail?.enabled) {
      return t('mfa.code');
    }
    
    return t('mfa.codeMFA');
  };

  const verifyCode = (props: {otp?: string}) => {
    const { otp = code } = props;

    if (!mfaAvailable?.length || !selectedMethod) {
      if (mfaAuthenticator?.enabled) {
        dispatch(authSlice.verifyAuthenticatorApp({ userId: id, token: otp }));
        return;
      }

      if (mfaEmail?.enabled) {
        dispatch(authSlice.verifyAuthenticatorEmail({ userId: id, code: otp }));
        return;
      }
    }

    switch (selectedMethod) {
      case MultifactorAuthenticationMethod.AUTHENTICATOR_APP:
        dispatch(authSlice.verifyAuthenticatorApp({ userId: id, token: otp }));
        break;
      
      case MultifactorAuthenticationMethod.EMAIL:
        dispatch(authSlice.verifyAuthenticatorEmail({ userId: id, code: otp }));
        break;
    }
  };

  const handleChange = (value: string) => {
    setCode(value);

    if (value.length === 6) {
      verifyCode({ otp: value });
    }
  };

  const renderCodeInput = () => {
    return (
      <Form.Item
        name='code'
        rules={[{ required: true, message: '' }]}
        label={getInputLabelCode()}
      >
        <LrvOtpInput
          value={code}
          containerStyle={styles.otpInput}
          onChange={handleChange}
          shouldAutoFocus={shouldAutoFocus}
          renderSeparator={<span>&nbsp;</span>}
          renderInput={(props) => <input {...props} />}
        />
      </Form.Item>
    );
  };

  const showResendCode = () => {
    if (mfaMethod !== MultifactorAuthenticationMethod.EMAIL && !selectedMethod) {
      return false;
    }

    if (selectedMethod && selectedMethod !== MultifactorAuthenticationMethod.EMAIL) {
      return false;
    }

    return true;
  };

  const renderResendCode = () => {
    if (!showResendCode()) {
      return null;
    }

    return (
      <div className={cx(styles.label, styles.obtainCode)}>
        <ActionButton
          type='text'
          loading={isFetchingVerificationCode}
          className={styles.button}
          onClick={() => {
            dispatch(authSlice.sendCodeToEmail({ userId: id }));
          }}
        >
          {t('signIn.obtainCode')}
        </ActionButton>
      </div>
    );
  };

  const renderVerificationMethods = () => {
    if (!mfaAvailable?.length) {
      return null;
    }

    return (
      <Form.Item>
        <div className={cx(styles.label, styles.verificationMethods)}>
          <div
            onClick={() => {
              dispatch(authSlice.setShowVerificationMethodModal(true));
              setShouldAutoFocus(false);
              setCode('');
              form.resetFields();
            }}
          >
            {t('signIn.verificationMethods')}
          </div>
        </div>
      </Form.Item>
    );
  };

  const renderError = () => {
    if (!errorSetupMfa) {
      return null;
    }

    return (
      <Form.Item>
        <div id='alert_error_login' className={cx(styles.label, styles.alertError)}>
          <div role='alert'>{errorSetupMfa}</div>
        </div>
      </Form.Item>
    );
  };

  const renderVerifyButton = () => {
    return (
      <Button
        id='verify-mfa'
        type='primary'
        loading={isLoadingSetupMfa}
        onClick={() => verifyCode({ otp: code })}
      >
        {t('signIn.verify')}
      </Button>
    );
  };

  return (
    <>
      <Form
        name='mfa-form'
        className={cx('loginForm', styles.loginForm)}
        layout='vertical'
        form={form}
      >
        {renderCodeInput()}
        {renderError()}

        <Form.Item>
          <Row justify={showResendCode() ? 'space-between' : 'end'} align='middle'>
            {renderResendCode()}
            {renderVerifyButton()}
          </Row>
        </Form.Item>

        {renderVerificationMethods()}
      </Form>

      <VerificationMethodForm />
    </>
  );
};
